// --------------------------------------------------------------
// Created On: 2026-03-13
// Author: Dimitra Weinstein
//
// Last Modified: 2026-03-23
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useEffect, useState } from "react";
import DeviceConfigurationModal from "./DeviceConfigurationModal/DeviceConfigurationModal";
import apiRequest from "src/utilities/api/apiRequest";
import {
  API,
  SECONDS_BETWEEN_DEVICE_READ_QUERIES,
  MAX_QUERIES_FOR_DEVICE_READ,
  MS_PER_SECOND,
} from "src/constants/miscellaneous";

// Button for opening the Device Configuration Modal and polling live configuration data.
export default function DeviceConfigurationButton(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [deviceConfigurationDetails, setDeviceConfigurationDetails] = useState<string>("");

  // Once a device is selected, sets a timer to poll the device configuration details.
  useEffect(() => {
    if (queriesRemaining > 0 && selectedDeviceId !== null && showModal) {
      const timerId = setTimeout(
        () => getDeviceConfigurationDetails(selectedDeviceId, queriesRemaining),
        MS_PER_SECOND * SECONDS_BETWEEN_DEVICE_READ_QUERIES
      );
      return () => clearTimeout(timerId);
    }
  }, [queriesRemaining, selectedDeviceId, showModal]);

  // Get the device configuration details for the give device ID. If the response is good then
  // show the device configuration details, otherwise reduce the number of queries remaining.
  async function getDeviceConfigurationDetails(deviceId: number | null, queriesRemaining: number): Promise<void> {
    if (deviceId !== null && queriesRemaining > 0) {
      const [response, responseBody] = (await apiRequest(`${API}/device/${deviceId}/config`, "GET", null)) as [
        Response,
        DeviceConfigurationRequestGetResponseBody
      ];
      if (response.ok && responseBody) {
        setDeviceConfigurationDetails(responseBody.configJson);
        setQueriesRemaining(0);
        setLoading(false);
      } else if (queriesRemaining <= 1) {
        setLoading(false);
        setSelectedDeviceId(null);
        setQueriesRemaining(0);
        setDeviceConfigurationDetails("");
        setErrorMessage("Unable to retrieve device configuration details.");
        setLoading(false);
      } else {
        setQueriesRemaining(queriesRemaining - 1);
      }
    }
  }

  // Sends a request to the monitoring device to return its configuration details.
  // This is an async process that could take a long time.
  function makeDeviceConfigurationRequest(): void {
    if (props.deviceId !== null) {
      void apiRequest(`${API}/device/${props.deviceId}/config`, "POST", null);
      setQueriesRemaining(MAX_QUERIES_FOR_DEVICE_READ);
      setSelectedDeviceId(props.deviceId);
    }
  }

  // Opens the Device Configuration Modal.
  function handleOpenModal(): void {
    setLoading(true);
    setShowModal(true);
    makeDeviceConfigurationRequest();
  }

  // Closes the Device Configuration Modal.
  function handleCloseModal(): void {
    setLoading(false);
    setShowModal(false);
    setErrorMessage("");
    setDeviceConfigurationDetails("");
  }

  return (
    <div>
      <button className="btn btn-primary" type="button" onClick={handleOpenModal} disabled={props.disabled}>
        View
      </button>
      <DeviceConfigurationModal
        showModal={showModal}
        loading={loading}
        deviceConfigurationDetails={deviceConfigurationDetails}
        errorMessage={errorMessage}
        onClose={handleCloseModal}
      />
    </div>
  );
}

interface Props {
  deviceId: number | null;
  disabled: boolean;
}

interface DeviceConfigurationRequestGetResponseBody {
  configJson: string;
}
